import React, { useState } from 'react';
import { Box, Button, FormField, Input, Modal, SpaceBetween } from '@amzn/awsui-components-react-v3';
import { associateTemplateToResources } from 'src/api/permissions';
import { ResourceSelector } from 'src/components/common/resourceSelector/resourceSelector';
import { TEMPLATE_TYPE_ACCESS_REQUEST_WORKFLOW } from 'src/commons/constants';
import { ResourceType } from 'src/components/common/resourceSelector/common';
import { AssociateTemplateToResourcesResult } from 'aws-sdk/clients/awsdatalakegladstonelambda';

export interface AssociateTemplateModalProps {
  templateId: string;
  setNotification: (header: any, message: string) => void;
  close: () => void;
  visible: boolean;
  activeGroup?: string;
  activeWorkspace?: any;
  handleRefresh?: () => void;
  templateType?: string;
}

export const AssociateTemplateModal = (props: AssociateTemplateModalProps) => {
  const [buttonLoading, setButtonLoading] = useState(false);
  const [resourceId, setResourceId] = useState(null);
  const [resourceIds, setResourceIds] = useState([]);
  const [resourceType, setResourceType] = useState(null);
  const [selectedResource, setSelectedResource] = useState(null);
  const isAccessRequestWorkflowTemplate: boolean = props.templateType === TEMPLATE_TYPE_ACCESS_REQUEST_WORKFLOW;

  async function invokeAPIToAssociate(resourceId: string): Promise<AssociateTemplateToResourcesResult> {
    let resourceOwnerId = undefined;
    if (resourceType == ResourceType.GOVERNANCE_TAG) {
      // For HC business glossary - governance tag, resourceOwnerId is required for associateTemplateToResources API
      resourceOwnerId = selectedResource?.OwnerId;
    }
    return await associateTemplateToResources({
      templateId: props.templateId,
      resourceId: resourceId,
      resourceOwnerId: resourceOwnerId,
    });
  }

  const handleAssociations = async () => {
    const isSelectInvalid: boolean = resourceId == undefined || resourceId == '';
    if (isSelectInvalid) {
      // multi-select
      await handleAssociateResources();
    } else {
      await handleAssociateResource();
    }
  };

  const handleAssociateResource = async () => {
    setButtonLoading(true);
    try {
      await invokeAPIToAssociate(resourceId);
      props.setNotification('success', `Template is associated with resource : ${resourceId}`);
      props.handleRefresh();
    } catch (err) {
      props.setNotification('error', `Failed to associate with resource ID: ${resourceId}, cause: ${err.message}`);
    }
    props.close();
    resetToDefault();
    setResourceId('');
    setResourceIds([]);
    setButtonLoading(false);
  };

  const handleAssociateResources = async () => {
    setButtonLoading(true);
    try {
      console.log(`Trying to associate ${resourceIds} to the template`);
      const allAssnResults: AssociateTemplateToResourcesResult[] = [];
      // run 5 associations concurrently at once
      for (let i = 0; i < resourceIds.length; i += 5) {
        const batch = resourceIds.slice(i, i + 5);
        const results = await Promise.all(batch.map(async (idOrArn) => await invokeAPIToAssociate(idOrArn)));
        results.forEach((res) => allAssnResults.push(res));
      }
      const successCount = allAssnResults
        .map((res) => res?.message?.toLowerCase())
        .filter((msg) => msg == 'success').length;
      if (successCount == allAssnResults.length) {
        props.setNotification('success', `Successfully associated ${successCount} resources to the template!`);
      } else {
        props.setNotification('error', `Failure in associating ${allAssnResults.length - successCount} resources!`);
      }
      props.handleRefresh();
    } catch (err) {
      props.setNotification('error', `Failed to associating resources!, cause: ${err.message}`);
    }
    props.close();
    resetToDefault();
    setResourceId('');
    setResourceIds([]);
    setButtonLoading(false);
  };

  const clearSelectedOptions = () => {
    setResourceId(null);
    setResourceIds([]);
  };

  const resetToDefault = () => {
    clearSelectedOptions();
  };

  const disableAssociateButton = () => {
    const isSelectInvalid: boolean = resourceId == undefined || resourceId == '';
    const isMultiSelectInvalid: boolean =
      !resourceIds || resourceIds.length == 0 || resourceIds.find((id) => id == undefined || id == '') != undefined;
    return isSelectInvalid && isMultiSelectInvalid;
  };

  return (
    <Modal
      onDismiss={() => {
        resetToDefault();
        props.close();
      }}
      visible={props.visible}
      size='medium'
      footer={
        <Box float='right'>
          <SpaceBetween direction='horizontal' size='xs'>
            <Button
              variant='link'
              onClick={() => {
                resetToDefault();
                props.close();
              }}
            >
              Cancel
            </Button>
            <Button
              variant='primary'
              disabled={disableAssociateButton()}
              onClick={handleAssociations}
              loading={buttonLoading}
            >
              {'Associate'}
            </Button>
          </SpaceBetween>
        </Box>
      }
      header='Associate resource to template'
    >
      <FormField label={'Template ID'}>
        <Input ariaRequired={true} value={props.templateId} readOnly={true} disabled />
      </FormField>
      <ResourceSelector
        {...props}
        setNotification={props.setNotification}
        onSelect={(resourceId) => setResourceId(resourceId)}
        onResourceSelect={(resource) => setSelectedResource(resource)}
        onResourceTypeSelect={(rType) => setResourceType(rType)}
        // For Access Request Workflow template, allow only Governance tags, DB, Table or FGAP.
        disableGroupAndWorkspaceSelection={isAccessRequestWorkflowTemplate}
        disableTagSelection={isAccessRequestWorkflowTemplate}
        disableMetadataFormSelection={true}
        disableCatalogSelection={isAccessRequestWorkflowTemplate}
        disableGovernanceTagSelection={!isAccessRequestWorkflowTemplate}
        enableMultiSelectForTableAndFga={true}
        onMultiSelect={(resourceIds) => setResourceIds(resourceIds)}
      />
    </Modal>
  );
};
